/**
 * Where a stored file's bytes come from.
 *
 * PGSS-5476/5477: the packing checklist stopped embedding documents in its response and started
 * sending a short-lived signed link instead. The thumbnail already coped, because it went through
 * getFileImageSrc — but the click-through viewers read `data` directly and broke: the image viewer
 * built `base64,undefined`, and the PDF viewer ran atob() over an https address.
 *
 * That was one function's worth of knowledge duplicated in three places and only corrected in one.
 * It lives here now so every caller resolves a file the same way.
 *
 * Deliberately its own module rather than part of AuraFunctions: AuraFunctions imports both viewers,
 * so the viewers importing it back would be a cycle.
 */

/**
 * The response names this field `Content-Type`. Older callers pass `content_type`, and some carry
 * only `mime`, so all three are accepted.
 */
export function contentTypeOf(media) {
	return media?.['Content-Type'] || media?.content_type || media?.mime;
}

/** True for an address the browser can fetch on its own, as opposed to inlined bytes. */
export function isHttpUrl(value) {
	return typeof value === 'string' && /^https?:\/\//i.test(value);
}

/**
 * The source for a stored file: the signed link when there is one, the inlined bytes otherwise.
 *
 * Returns null when neither is present, so a caller can tell "nothing to show" apart from a source
 * that will fail once the browser tries to load it.
 */
export function resolveFileSrc(media) {
	if (!media) {
		return null;
	}
	if (media.url) {
		return media.url;
	}
	if (!media.data) {
		return null;
	}
	return `data:${contentTypeOf(media)};base64,${media.data}`;
}

/**
 * The source fields to copy when one media object is folded into another.
 *
 * PGSS-5505. Callers did this by hand as `file.url.split(',')[1]`, which assumed the url was always
 * a data URI. Once an upload or an etching carries a signed link instead, that split yields
 * undefined and the link itself was never copied -- so the target ended up with no source at all
 * and rendered blank. It also threw outright when `url` was absent.
 *
 * Returns both fields so a caller can spread them in one go: `url` for anything that renders, and
 * `data` for the callers that still post bytes. A signed link has no bytes to give and says so with
 * `data: null`, rather than leaving a stale value from whatever the target held before.
 */
export function mediaSourceOf(media) {
	const mime = contentTypeOf(media);
	const base = mime ? { 'Content-Type': mime } : {};
	const src = resolveFileSrc(media);
	if (!src) {
		return { ...base, url: null, data: null };
	}
	if (isHttpUrl(src)) {
		return { ...base, url: src, data: null };
	}
	const comma = src.indexOf(',');
	return { ...base, url: src, data: comma >= 0 ? src.slice(comma + 1) || null : null };
}

/**
 * The tile for a file the user has just uploaded, built from what the upload STORED.
 *
 * PGSS-5483 / PGSS-5505: preview the stored file (an iPhone photo is converted on upload), from
 * whichever source the response carries.
 *
 * PGSS-5577: `download_url` is carried across too. It is the link signed to come back with
 * Content-Disposition: attachment; without it Download on a file uploaded in this dialog fell back
 * to opening `url` in a new tab -- the same thing View does.
 *
 * Returns null when the response carries no usable source, so the caller can fall back to the
 * local file.
 */
export function uploadedAttachmentOf(saved, type) {
	if (!(saved?.url || (saved?.data && saved?.['Content-Type']))) {
		return null;
	}
	return {
		id: saved?.id,
		url: resolveFileSrc(saved),
		download_url: saved?.download_url,
		'Content-Type': saved['Content-Type'],
		type,
		image_uuid: saved?.etchingUUID,
		name: saved?.filename,
	};
}
